import React, { useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Fade from '@mui/material/Fade';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import LogoutRounded from '@mui/icons-material/LogoutRounded';

import TopBar, { SIDEBAR_WIDTH } from './TopBar';
import FloatingChat from '../chat/FloatingChat';
import useAuth from '../../hooks/useAuth';
import { ROLES, ROLE_LABELS } from '../../config/constants';
import gblLogo from '../../images/gbl logo.png';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardOutlined />, roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ASSOCIATE] },
  { label: 'Students', path: '/students', icon: <SchoolOutlined />, roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ASSOCIATE] },
  { label: 'Users', path: '/users', icon: <PeopleOutlined />, roles: [ROLES.ADMIN] },
  { label: 'Bankers', path: '/bankers', icon: <AccountBalanceOutlined />, roles: [ROLES.ADMIN] },
  { label: 'Consultancy', path: '/consultancies', icon: <HandshakeOutlined />, roles: [ROLES.ADMIN] },
  { label: 'Banker Portal', path: '/banker-portal', icon: <FolderOutlined />, roles: [ROLES.BANKER] },
  { label: 'Consultancy Portal', path: '/consultancy-portal', icon: <HandshakeOutlined />, roles: [ROLES.CONSULTANCY] },
  { label: 'Reports', path: '/reports', icon: <AssessmentOutlined />, roles: [ROLES.ADMIN, ROLES.MANAGER] },
  { label: 'Calculator', path: '/calculator', icon: <CalculateOutlined />, roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ASSOCIATE, ROLES.BANKER] },
  { label: 'Settings', path: '/settings', icon: <SettingsOutlined />, roles: [ROLES.ADMIN] },
];

const SidebarNavItem = ({ item, active, onClick }) => (
  <Tooltip title={item.label} placement="right" arrow>
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        py: 1,
        px: 0.5,
        borderRadius: 2,
        color: active ? '#1A73E8' : '#5F6368',
        backgroundColor: active ? 'rgba(26,115,232,0.08)' : 'transparent',
        transition: 'all 0.15s',
        '&:hover': {
          color: '#1A73E8',
          backgroundColor: active ? 'rgba(26,115,232,0.12)' : '#F1F3F4',
        },
      }}
    >
      {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
      <Typography
        sx={{
          fontSize: '0.5625rem',
          fontWeight: active ? 600 : 500,
          lineHeight: 1.1,
          mt: 0.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          textAlign: 'center',
        }}
      >
        {item.label}
      </Typography>
    </ButtonBase>
  </Tooltip>
);

const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { mongoUser, role, logout } = useAuth();

  const filteredNav = useMemo(
    () => NAV_ITEMS.filter((item) => item.roles.includes(role)),
    [role]
  );

  const isActive = (item) =>
    location.pathname === item.path || location.pathname.startsWith(item.path + '/');

  const getInitials = () => {
    const name = mongoUser?.name || '';
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  };

  const handleLogout = async () => {
    try { await logout(); } catch (err) { console.error('Logout failed:', err); }
  };

  /* Desktop sidebar: slim icon rail */
  const desktopSidebar = (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#fff',
          borderRight: '1px solid #DADCE0',
          overflow: 'hidden',
        },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 56,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/dashboard')}
      >
        <Box
          component="img"
          src={gblLogo}
          alt="GBL Tree"
          sx={{ height: 28, width: 'auto' }}
        />
      </Box>

      <Divider sx={{ borderColor: '#F1F3F4' }} />

      {/* Nav items */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          px: 0.75,
          py: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 0 },
        }}
      >
        {filteredNav.map((item) => (
          <SidebarNavItem
            key={item.path}
            item={item}
            active={isActive(item)}
            onClick={() => navigate(item.path)}
          />
        ))}
      </Box>

      {/* Bottom: avatar + logout */}
      <Divider sx={{ borderColor: '#F1F3F4' }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1, gap: 0.5 }}>
        <Tooltip title={mongoUser?.name || 'User'} placement="right" arrow>
          <Avatar
            sx={{ width: 28, height: 28, bgcolor: '#1A73E8', fontSize: '0.7rem', fontWeight: 500 }}
            src={mongoUser?.avatar}
          >
            {getInitials()}
          </Avatar>
        </Tooltip>
        <Tooltip title="Logout" placement="right" arrow>
          <IconButton
            onClick={handleLogout}
            size="small"
            sx={{ color: '#5F6368', '&:hover': { color: '#D93025' } }}
          >
            <LogoutRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Drawer>
  );

  /* Mobile sidebar: full drawer with labels */
  const mobileSidebar = (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': {
          width: 260,
          backgroundColor: '#fff',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Box component="img" src={gblLogo} alt="GBL Tree" sx={{ height: 28 }} />
        <IconButton onClick={() => setMobileOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ px: 1, pt: 1 }}>
        {filteredNav.map((item) => {
          const active = isActive(item);
          return (
            <ListItemButton
              key={item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1,
                color: active ? '#1A73E8' : '#3C4043',
                backgroundColor: active ? 'rgba(26,115,232,0.08)' : 'transparent',
                '&:hover': { backgroundColor: active ? 'rgba(26,115,232,0.12)' : '#F1F3F4' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: active ? '#1A73E8' : '#5F6368' }}>
                {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', px: 2, py: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: '#1A73E8', fontSize: '0.7rem' }} src={mongoUser?.avatar}>
            {getInitials()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#202124' }}>
              {mongoUser?.name || 'User'}
            </Typography>
            <Typography sx={{ fontSize: '0.6875rem', color: '#5F6368', textTransform: 'capitalize' }}>
              {ROLE_LABELS[role] || role}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {desktopSidebar}
      {mobileSidebar}

      <TopBar onMenuClick={() => setMobileOpen((prev) => !prev)} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: `${SIDEBAR_WIDTH}px` },
          width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Spacer for fixed AppBar */}
        <Box sx={{ height: 56 }} />

        <Fade in timeout={300}>
          <Box
            sx={{
              flexGrow: 1,
              px: { xs: 2, sm: 3, md: 3 },
              py: 2.5,
              maxWidth: 1400,
              width: '100%',
            }}
          >
            <Outlet />
          </Box>
        </Fade>
      </Box>

      <FloatingChat />
    </Box>
  );
};

export default MainLayout;
